<script setup lang="ts">
import {onMounted} from 'vue'
import {RouterLink} from 'vue-router'
import {useCookieBanner} from '@/composables/www/useCookieBanner'
import {needsMarketingConsentDecision, setMarketingConsent} from '@/lib/analytics'
import type {MarketingConsent} from '@/lib/analytics'

/**
 * Low-profile cookie consent card, bottom-right of the www site (bottom-left would sit on the
 * footer's "Cookie preferences" link that reopens it).
 *
 * Opens on first visit only (`needsMarketingConsentDecision`); a stored choice or a Global
 * Privacy Control signal keeps it closed. Marketing tags already run under the opt-out default,
 * so the only thing a click changes is Reject. The footer's "Cookie preferences" link reopens it.
 * Rendered only after mount so the SSR snapshot and the hydrating client agree on an empty slot.
 */
const {cookieBannerOpen, open, close} = useCookieBanner()

onMounted(() => {
    if (needsMarketingConsentDecision()) open()
})

function choose(value: MarketingConsent) {
    setMarketingConsent(value)
    close()
}
</script>

<template>
  <Transition name="cookie-banner">
    <section
        v-if="cookieBannerOpen"
        class="cookie-banner"
        role="region"
        aria-label="Cookie preferences"
        data-cookie-banner
    >
      <p class="cookie-banner__text">
        We use cookies for analytics and marketing.
        <RouterLink class="cookie-banner__link" to="/legal/privacy-policy">Privacy policy</RouterLink>
      </p>
      <div class="cookie-banner__actions">
        <button
            type="button"
            class="cookie-banner__button cookie-banner__button--reject"
            data-cookie-banner-reject
            @click="choose('denied')"
        >
          Reject marketing
        </button>
        <button
            type="button"
            class="cookie-banner__button cookie-banner__button--accept"
            data-cookie-banner-accept
            @click="choose('granted')"
        >
          Accept
        </button>
      </div>
    </section>
  </Transition>
</template>

<style scoped>
.cookie-banner {
    position: fixed;
    right: var(--space-5);
    bottom: var(--space-5);
    z-index: 60;
    display: flex;
    flex-direction: column;
    gap: var(--space-4);
    /* Sized from the viewport edges, not 100vw: with classic (non-overlay) scrollbars 100vw
       includes the scrollbar and the card would overhang the visible area. */
    max-width: 360px;
    padding: var(--space-5);
    background-color: var(--surface);
    color: var(--text-primary);
    border: 1px solid var(--border);
    border-radius: var(--radius-md);
    box-shadow: var(--shadow-md);
    font-family: var(--font-body);
}

.cookie-banner__text {
    margin: 0;
    font-size: 13px;
    line-height: 1.5;
    color: var(--text-secondary);
}

.cookie-banner__link {
    color: var(--primary-500);
    text-decoration: underline;
    text-underline-offset: 2px;
    white-space: nowrap;
}

.cookie-banner__actions {
    display: flex;
    justify-content: flex-end;
    gap: var(--space-3);
}

.cookie-banner__button {
    padding: var(--space-2) var(--space-5);
    font-size: 13px;
    font-weight: var(--fw-medium);
    line-height: 1.4;
    border-radius: var(--radius-button);
    border: 1px solid transparent;
    cursor: pointer;
    transition:
        background-color 0.18s var(--ease-out-strong),
        border-color 0.18s var(--ease-out-strong),
        color 0.18s var(--ease-out-strong);
}

.cookie-banner__button--accept {
    background-color: var(--button-default);
    color: var(--text-white);
}

.cookie-banner__button--reject {
    background-color: transparent;
    color: var(--text-primary);
    border-color: var(--border);
}

@media (hover: hover) and (pointer: fine) {
    .cookie-banner__button--accept:hover {
        background-color: var(--button-active);
    }

    .cookie-banner__button--reject:hover {
        border-color: var(--neutral-400);
    }
}

.cookie-banner__button:focus-visible,
.cookie-banner__link:focus-visible {
    outline: 2px solid var(--focus);
    outline-offset: 2px;
}

.cookie-banner-enter-active,
.cookie-banner-leave-active {
    transition:
        opacity 220ms var(--ease-out-strong),
        transform 220ms var(--ease-out-strong);
}

.cookie-banner-enter-from,
.cookie-banner-leave-to {
    opacity: 0;
    transform: translateY(8px);
}

@media (max-width: 480px) {
    .cookie-banner {
        left: var(--space-4);
        right: var(--space-4);
        bottom: var(--space-4);
        max-width: none;
    }

    .cookie-banner__actions {
        justify-content: stretch;
    }

    .cookie-banner__button {
        flex: 1;
    }
}

@media (prefers-reduced-motion: reduce) {
    .cookie-banner-enter-active,
    .cookie-banner-leave-active,
    .cookie-banner__button {
        transition: none;
    }
}
</style>
